import { jsx as _jsx } from "react/jsx-runtime";
import { configurationSettings, createStandardOrder, getAreaCategoriesForSeatLayout, getOrderId, getOrderSeatSelectionLimitsForShowtime, getOrderTicketCountForShowtime, getReservedOrderSeatIdsForShowtime, getScreenById, getSeatAvailabilityForShowtime, getSeatLayoutById, hasOrderBeenCreated, loadSeatAvailabilityForShowtime, loadSeatLayoutForShowtime, logger, mapSeatToOrderSeat, orderHasInProgressAction, saveOrderSeatsForShowtime, setOrderSeatsForShowtime, TicketingMode } from '@vista/omnichannel-components-domain';
import { SeatLayoutForShowtimeErrorCodeV1 } from '@vista-digital/ocapi-client';
import { observer } from 'mobx-react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useOccConfig } from '../../../context/occ/hooks/use-occ-config';
import { useShowtimeContext } from '../../../context/showtime/hooks/use-showtime-context';
import { withComponentWrapper } from '../../../utils/with-component-wrapper';
import { SeatPicker } from '../../seat-picker/seat-picker';
import { SeatPickerSkeleton } from '../../seat-picker/subcomponents/skeleton';
import { OrderSeatPickerMessage, OrderSeatPickerMessageType } from './subcomponents/message';
import { SelectedSeatIdsProvider } from './subcomponents/selected-seat-ids-provider';
import { defaultOrderSeatPickerProps, orderSeatPickerPropsValidator } from './types/props';
import { addProceedErrorNotification, removeProceedErrorNotification } from './utils/notification';
const OrderSeatPickerBase = (props, ref) => {
    const { enableSkeletonLoadingIndicators } = useOccConfig();
    const { friendSeatIds, events, displayText } = props;
    const { showtimeId, showtime } = useShowtimeContext();
    const inputRef = useRef(null);
    const [apiErrorMessageType, setApiErrorMessageType] = useState();
    const { onSelectedSeatsChanged } = events;
    const { ticketingMode } = configurationSettings.data.order;
    const isSeatFirstOrdering = ticketingMode === TicketingMode.SeatFirst;
    if (!isSeatFirstOrdering && getOrderTicketCountForShowtime(showtimeId) === 0) {
        throw new Error('Cannot render the Order Seat Picker in ticket-first ordering mode when no tickets have been selected.');
    }
    // The orderId is intentionally excluded from the dependencies to prevent the seat picker from re-loading when the order is created via handleProceed.
    useEffect(() => {
        setApiErrorMessageType(undefined);
        Promise.all([
            loadSeatLayoutForShowtime(showtimeId),
            loadSeatAvailabilityForShowtime(showtimeId, getOrderId())
        ]).catch(err => setApiErrorMessageType(handleApiError(err)));
    }, [showtimeId]);
    useImperativeHandle(ref, () => ({
        validateSeatSelections: () => {
            return inputRef.current.validateSeatSelections();
        },
        handleProceed: async () => handleProceed()
    }));
    const seatingData = getSeatingDataForShowtime(showtime, friendSeatIds);
    const handleProceed = async () => {
        try {
            removeProceedErrorNotification();
            if (!seatingData)
                throw new Error('Cannot handle proceed without seating data');
            const validationResult = inputRef.current.validateSeatSelections();
            if (validationResult.isValid) {
                if (!hasOrderBeenCreated())
                    await createStandardOrder(seatingData.siteId);
                await saveOrderSeatsForShowtime(showtimeId);
                return true;
            }
        }
        catch (error) {
            addProceedErrorNotification(displayText, error);
        }
        return false;
    };
    const handleSelectedSeatsChanged = useCallback((seats) => {
        if (!seatingData)
            throw new Error('Cannot handle selected seats changed without seating data');
        setOrderSeatsForShowtime(showtimeId, seats.map(seat => mapSeatToOrderSeat(seat, seatingData.seatLayout.areas)));
        onSelectedSeatsChanged === null || onSelectedSeatsChanged === void 0 ? void 0 : onSelectedSeatsChanged(seats);
    }, [onSelectedSeatsChanged, seatingData, showtimeId]);
    const mappedEvents = useMemo(() => (Object.assign(Object.assign({}, props.events), { onSelectedSeatsChanged: handleSelectedSeatsChanged })), [props.events, handleSelectedSeatsChanged]);
    const seatAvailability = getSeatAvailabilityForShowtime(showtimeId);
    if (seatAvailability === null || seatAvailability === void 0 ? void 0 : seatAvailability.isSoldOut) {
        return _jsx(OrderSeatPickerMessage, Object.assign({}, props, { messageType: OrderSeatPickerMessageType.SoldOut }));
    }
    if (apiErrorMessageType) {
        return _jsx(OrderSeatPickerMessage, Object.assign({}, props, { messageType: apiErrorMessageType }));
    }
    if (!seatingData)
        return enableSkeletonLoadingIndicators ? _jsx(SeatPickerSkeleton, {}) : null;
    const isDisabled = orderHasInProgressAction();
    return (_jsx(SelectedSeatIdsProvider, { showtimeId: showtimeId, render: selectedSeatIds => (_jsx(SeatPicker, Object.assign({}, props, { events: mappedEvents, ref: inputRef, data: seatingData, selectedSeatIds: selectedSeatIds, isDisabled: isDisabled }))) }));
};
const getSeatingDataForShowtime = (showtime, friendSeatIds) => {
    if (!showtime)
        return; // Showtime still loading
    if (!showtime.isAllocatedSeating) {
        throw new Error(`Cannot render Order Seat Picker for unallocated showtime ${showtime.id}.`);
    }
    const { screenId, seatLayoutId, siteId } = showtime;
    const screen = getScreenById(screenId);
    if (!screen)
        throw new Error(`Cannot find screen with ID: ${screenId}.`);
    const seatLayout = getSeatLayoutById(seatLayoutId);
    const seatAvailability = getSeatAvailabilityForShowtime(showtime.id);
    if (!seatLayout || !seatAvailability)
        return; // Data still loading
    const areaCategories = getAreaCategoriesForSeatLayout(seatLayoutId);
    const reservedSeatIds = getReservedOrderSeatIdsForShowtime(showtime.id);
    const seatSelectionLimits = getOrderSeatSelectionLimitsForShowtime(showtime.id, areaCategories.map(ac => ac.id));
    return {
        areaCategories,
        reservedSeatIds,
        friendSeatIds,
        screen,
        seatAvailabilities: seatAvailability.seatAvailabilities,
        seatLayout,
        seatSelectionLimits,
        siteId
    };
};
const handleApiError = (error) => {
    logger.error(error.message);
    switch (error.errorCode) {
        case SeatLayoutForShowtimeErrorCodeV1.SoldOut: {
            return OrderSeatPickerMessageType.SoldOut;
        }
        default: {
            return OrderSeatPickerMessageType.FailedToLoadData;
        }
    }
};
export const OrderSeatPicker = observer(forwardRef(OrderSeatPickerBase));
OrderSeatPicker.displayName = 'OrderSeatPicker';
/**
 * The **Order Seat Picker** component renders a **Seat Picker** component for a specific showtime and updates the
 * selected seats on the order based on the user's input.
 */
export const OrderSeatPickerComponent = withComponentWrapper(OrderSeatPicker, defaultOrderSeatPickerProps, orderSeatPickerPropsValidator);
